
<template>
  <div class="dynamic-detail-page">
    <!-- 导航栏 -->
    <van-nav-bar
      :title="$t('dynamic.detail')"
      left-arrow
      fixed
      placeholder
      @click-left="$router.back()"
    />

    <!-- 加载状态 -->
    <div
      v-if="loading"
      class="loading-container"
    >
      <van-loading
        size="24px"
        vertical
      >
        {{ $t('dynamic.loading') }}
      </van-loading>
    </div>
    <!-- 内容区域 -->
    <div
      v-else-if="dynamicData"
      class="content-container"
    >
      <!-- 封面图 -->
      <div
        v-if="dynamicData.coverPic"
        class="cover-section"
      >
        <img
          :src="dynamicData.coverPic"
          :alt="dynamicData.title"
          class="cover-image"
          @load="handleImageLoad"
          @error="handleImageError"
        >
        <div class="cover-overlay" />
      </div>

      <!-- 主要内容 -->
      <div class="main-content">
        <!-- 标题区域 -->
        <div class="title-section">
          <h1 class="dynamic-title">
            {{ dynamicData.title }}
          </h1>
          <p class="dynamic-description">
            {{ dynamicData.description }}
          </p>

          <!-- 标签区域 -->
          <div
            v-if="dynamicData.tags && dynamicData.tags.length"
            class="tags-section"
          >
            <van-tag
              v-for="tag in dynamicData.tags"
              :key="tag"
              type="primary"
              size="medium"
              class="dynamic-tag"
            >
              {{ tag }}
            </van-tag>
          </div>

          <!-- 元信息 -->
          <div class="meta-info">
            <div class="meta-item">
              <van-icon name="user-circle-o" />
              <span>{{ dynamicData.createdBy }}</span>
            </div>
            <div class="meta-item">
              <van-icon name="clock-o" />
              <span>{{ formatDate(dynamicData.createdTime) }}</span>
            </div>
            <div class="meta-item">
              <van-icon name="eye-o" />
              <span>{{ dynamicData.readCount }} {{ $t('dynamic.views') }}</span>
            </div>
<!--            <div-->
<!--              v-if="dynamicData.isTop"-->
<!--              class="meta-item"-->
<!--            >-->
<!--              <van-icon name="certificate" />-->
<!--              <span>{{ $t('dynamic.pinned') }}</span>-->
<!--            </div>-->
          </div>
        </div>

        <!-- 内容区域 -->
        <div class="content-section">
          <div
            class="dynamic-content"
            v-html="dynamicData.content"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { showToast } from 'vant'
import { queryPromotionDetail } from '@/api/promotion'
import { getUserLang } from '@/storage/user'
export default {
  name: 'PromotionDetail',
  setup () {
    const { t } = useI18n()
    const router = useRouter()
    const route = useRoute()

    // 响应式数据
    const loading = ref(true)
    const error = ref(false)
    const dynamicData = ref<any>(null)
    const likeCount = ref(0)
    const imageLoaded = ref(false)

    // 方法
    const getPromotionDetail = async () => {
      loading.value = true
      error.value = false
      queryPromotionDetail(promotionId.value).then(res => {
        dynamicData.value = res
      }).catch(() => {
        error.value = true
        showToast(t('dynamic.loadFailed'))
      })
    }

    const formatDate = (dateString: string) => {
      const date = new Date(dateString)
      const lang = getUserLang()
      return new Intl.DateTimeFormat(lang, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    }

    const handleImageLoad = () => {
      imageLoaded.value = true
    }

    const handleImageError = () => {
      // 图片加载失败的处理
      console.error('封面图加载失败')
    }

    const promotionId = ref(0)
    // 生命周期
    onMounted(() => {
      promotionId.value = parseInt(route.query.id as string || '0')
      getPromotionDetail()
    })
    return {
      dynamicData,
      likeCount,
      imageLoaded,
      formatDate,
      handleImageLoad,
      handleImageError
    }
  }
}

</script>

<style lang="scss" scoped>
.dynamic-detail-page {
  min-height: 100vh;
  background-color: #f7f8fa;
}

.loading-container,
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.content-container {
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

/* 封面图区域 */
.cover-section {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 40%; /* 封面图比例 */
  overflow: hidden;

  .cover-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .cover-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60%;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.3));
  }
}

/* 主要内容区域 */
.main-content {
  margin-top: -20px;
  position: relative;
  z-index: 2;
}

.title-section {
  background: white;
  padding: 24px 20px 20px;
  border-radius: 16px 16px 0 0;
  margin: 0 16px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);

  .dynamic-title {
    margin: 0 0 12px;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.4;
    color: #1a1a1a;
  }

  .dynamic-description {
    margin: 0 0 16px;
    font-size: 16px;
    line-height: 1.6;
    color: #666;
  }
}

.tags-section {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;

  .dynamic-tag {
    border-radius: 12px;
  }
}

.meta-info {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  .meta-item {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #999;

    .van-icon {
      margin-right: 4px;
      font-size: 14px;
    }
  }
}

/* 内容区域 */
.content-section {
  background: white;
  margin: 0 16px;
  padding: 20px;
  //text-align: left;

  :deep(.dynamic-content) {
    font-size: 16px;
    line-height: 1.8;
    color: #333;

    h1, h2, h3, h4, h5, h6 {
      margin: 24px 0 16px;
      font-weight: 600;
      line-height: 1.4;
      color: #1a1a1a;
    }

    h1 {
      font-size: 22px;
      border-bottom: 1px solid #f0f0f0;
      padding-bottom: 8px;
    }

    h2 {
      font-size: 20px;
    }

    h3 {
      font-size: 18px;
    }

    p {
      margin: 0 0 16px;
    }

    ul, ol {
      margin: 0 0 16px;
      padding-left: 24px;

      li {
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    ul {
      list-style-type: disc;
    }

    ol {
      list-style-type: decimal;
    }

    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 16px 0;
    }

    blockquote {
      border-left: 4px solid #1989fa;
      background: #f8f9fa;
      margin: 16px 0;
      padding: 12px 16px;
      border-radius: 0 8px 8px 0;

      p {
        margin: 0;
        color: #666;
        font-style: italic;
      }
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 16px 0;

      th, td {
        padding: 12px;
        border: 1px solid #e8e8e8;
        text-align: left;
      }

      th {
        background: #f8f9fa;
        font-weight: 600;
      }
    }
  }
}

/* 1080×1920 设备适配 (竖屏大屏设备) */
@media (min-width: 1080px) and (min-height: 1920px) {
  .cover-section {
    padding-bottom: 45%; /* 调整封面图比例 */
  }

  .title-section {
    padding: 32px 24px 24px;
    margin: 0 24px;

    .dynamic-title {
      font-size: 28px;
      margin-bottom: 16px;
    }

    .dynamic-description {
      font-size: 18px;
      margin-bottom: 20px;
    }
  }

  .meta-info {
    gap: 20px;

    .meta-item {
      font-size: 15px;

      .van-icon {
        font-size: 16px;
      }
    }
  }

  .content-section {
    margin: 0 24px;
    padding: 24px;

    :deep(.dynamic-content) {
      font-size: 18px;
      line-height: 1.9;

      h1 {
        font-size: 26px;
      }

      h2 {
        font-size: 24px;
      }

      h3 {
        font-size: 22px;
      }

      p, ul, ol {
        margin-bottom: 20px;
      }
    }
  }

  .action-section {
    margin: 0 24px 24px;
    padding: 24px;

    .action-btn {
      height: 48px;
      font-size: 16px;
      padding: 0 24px;
    }
  }

  .recommend-section {
    margin: 0 24px 24px;
    padding: 24px;

    .section-title {
      font-size: 20px;
      margin-bottom: 20px;
    }

    .recommend-item {
      padding: 16px;

      .recommend-image {
        width: 100px;
        height: 75px;
      }

      .recommend-title {
        font-size: 17px;
      }

      .recommend-desc {
        font-size: 15px;
      }

      .recommend-meta {
        font-size: 13px;
      }
    }
  }
}

/* 平板设备 (768px - 1199px) */
@media (max-width: 1199px) and (min-width: 768px) {
  .cover-section {
    padding-bottom: 35%;
  }

  .title-section {
    margin: 0 20px;
    padding: 20px;
  }

  .content-section {
    margin: 0 20px;
    padding: 20px;
  }

  .action-section {
    margin: 0 20px 20px;
  }

  .recommend-section {
    margin: 0 20px 20px;
  }
}

/* 移动端适配 (767px 及以下) */
@media (max-width: 767px) {
  .cover-section {
    padding-bottom: 50%;
  }

  .title-section {
    margin: 0 12px;
    padding: 16px;

    .dynamic-title {
      font-size: 20px;
    }

    .dynamic-description {
      font-size: 15px;
    }
  }

  .meta-info {
    gap: 12px;

    .meta-item {
      font-size: 12px;
    }
  }

  .content-section {
    margin: 0 12px;
    padding: 16px;

    :deep(.dynamic-content) {
      font-size: 15px;

      h1 {
        font-size: 20px;
      }

      h2 {
        font-size: 18px;
      }

      h3 {
        font-size: 16px;
      }
    }
  }

  .action-section {
    margin: 0 12px 12px;
    padding: 16px;
    gap: 12px;

    .action-btn {
      padding: 0 16px;
      height: 36px;
      font-size: 13px;
    }
  }

  .recommend-section {
    margin: 0 12px 12px;
    padding: 16px;

    .recommend-item {
      padding: 10px;

      .recommend-image {
        width: 70px;
        height: 52px;
      }

      .recommend-title {
        font-size: 14px;
      }

      .recommend-desc {
        font-size: 12px;
      }
    }
  }
}

/* 超小屏幕设备 (480px 及以下) */
@media (max-width: 480px) {
  .cover-section {
    padding-bottom: 60%;
  }

  .title-section {
    margin: 0 8px;
    padding: 12px;

    .dynamic-title {
      font-size: 18px;
    }
  }

  .meta-info {
    flex-direction: column;
    gap: 8px;
  }

  .content-section {
    margin: 0 8px;
    padding: 12px;
  }

  .action-section {
    margin: 0 8px 8px;
    padding: 12px;
    flex-direction: column;
    align-items: stretch;

    .action-btn {
      width: 100%;
    }
  }

  .recommend-section {
    margin: 0 8px 8px;
    padding: 12px;
  }
}

/* 横屏模式适配 */
@media (max-height: 500px) and (orientation: landscape) {
  .cover-section {
    padding-bottom: 30%;
  }

  .title-section {
    padding: 16px;
  }

  .content-section {
    padding: 16px;
  }

  .action-section {
    padding: 16px;
  }
}

/* 暗色模式支持 */
@media (prefers-color-scheme: dark) {
  .dynamic-detail-page {
    background-color: #1a1a1a;
  }

  .title-section,
  .content-section,
  .action-section,
  .recommend-section {
    background: #2a2a2a;
  }

  .title-section .dynamic-title,
  .content-section :deep(.dynamic-content) h1,
  .content-section :deep(.dynamic-content) h2,
  .content-section :deep(.dynamic-content) h3,
  .recommend-section .section-title,
  .recommend-item .recommend-title {
    color: #ffffff;
  }

  .title-section .dynamic-description,
  .content-section :deep(.dynamic-content),
  .content-section :deep(.dynamic-content) p,
  .recommend-item .recommend-desc {
    color: #cccccc;
  }

  .meta-info .meta-item,
  .recommend-item .recommend-meta {
    color: #999999;
  }

  .recommend-item {
    background: #333333;

    &:hover {
      background: #3a3a3a;
    }
  }

  .content-section :deep(.dynamic-content) blockquote {
    background: #333333;
    color: #cccccc;
  }

  .content-section :deep(.dynamic-content) table {
    th, td {
      border-color: #444444;
    }

    th {
      background: #333333;
    }
  }
}
</style>
